import { isEmpty } from 'lodash';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Icon, IconButton, Stack } from '@common-components';
import { AutoAwesomeOutlined } from '@icons';
import { AIMessageType } from 'enums';
import { useGenerateSubmissionAIThread, useOptionsMenu } from 'hooks';
import { messages } from 'i18n';
import { softShadow } from 'themes';
import { ExtendedBoxItem } from 'broker/components/FilesExplorer/types';
import AIPopup from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIPopup';
import {
  popupMaxHeight,
  popupMinHeight,
  popupWidth,
} from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/constants';
import { RegenerateButtonMode } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/RegenerateButton';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useUiStoreActions, useUiStoreState } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { useAIAssistantTriggerListener } from './useAIAssistantTriggerListener';

export function SubmissionAIAssistant() {
  const { anchorEl, isMenuOpen, openMenu, closeMenu } = useOptionsMenu();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const {
    isLoading,
    isContentStreaming,
    threadId,
    createAIThread,
    messages: aiMessages,
    resetAIThread,
  } = useAIContext();
  const { submission } = useSubmissionsWorkspace();
  const { filesExplorer } = useUiStoreState();
  const { setFilesExplorer, resetAIAssistantFeature } = useUiStoreActions();
  const { generateSubmissionAIThread } = useGenerateSubmissionAIThread();

  // Used for backing up the original selection mode of the files explorer so we can restore it after the files are selected
  const [originalSelectionMode, setOriginalSelectionMode] = useState<boolean | undefined>(undefined);

  // Local state for managing selected files within this component - completely separate from global state
  const [localSelectedFiles, setLocalSelectedFiles] = useState<ExtendedBoxItem[]>([]);

  // Local flag to track when we're in AI assistant file collection mode
  const [isInFileCollectionMode, setIsInFileCollectionMode] = useState(false);

  const emptyMessages = isEmpty(aiMessages);

  const handleAttachFiles = useCallback(() => {
    // Backup the original selection mode
    setOriginalSelectionMode(filesExplorer.selectionMode || false);
    // Set local flag to indicate we're in AI assistant file collection mode
    setIsInFileCollectionMode(true);
    // Sync local selected files back to global state before entering selection mode
    // so they appear as selected when the user navigates to the file explorer
    setFilesExplorer({ selectionMode: true, selectedFiles: localSelectedFiles });
    closeMenu();
  }, [closeMenu, filesExplorer.selectionMode, setFilesExplorer, setOriginalSelectionMode, localSelectedFiles]);

  const [shouldAutoRun, setShouldAutoRun] = useState(false);

  const resetState = useCallback(() => {
    resetAIThread();
    setLocalSelectedFiles([]);
  }, [resetAIThread]);

  const { initialQuestion, resetTriggerState, aiFeature, quoteId } = useAIAssistantTriggerListener({
    assistantButtonRef: buttonRef,
    onTrigger: () => {
      resetState();
      handleAttachFiles();
      setShouldAutoRun(true);
    },
  });

  const handleRegenerate = useCallback(() => {
    resetState();
    resetTriggerState();
    resetAIAssistantFeature();
    setShouldAutoRun(false);
  }, [resetState, resetTriggerState, setShouldAutoRun, resetAIAssistantFeature]);

  // Track changes in selectionMode
  useEffect(() => {
    // If we're in file collection mode and selection mode is turned off externally,
    // we should exit file collection mode
    if (isInFileCollectionMode && !filesExplorer.selectionMode) {
      setIsInFileCollectionMode(false);
      setOriginalSelectionMode(undefined);
    }
  }, [filesExplorer.selectionMode, isInFileCollectionMode]);

  // In the submission AI assistant there is unique usage where the first followup message is not really a followup message
  // but rather the initial thread creation (enriched by the user prompt and the files attached).
  // Therefore we use a special handler to override the default behavior when the user enters a message.
  const handleFirstFollowupMessage = useCallback(
    async (message: string) => {
      const userPromptMessage = { type: AIMessageType.Prompt, content: message };
      await createAIThread(
        () =>
          generateSubmissionAIThread({
            submissionId: submission!.id,
            boxFileIds: localSelectedFiles.map((file) => file.id),
            initialUserMessage: message,
            aiFeature,
            quoteId,
          }),
        userPromptMessage, // Override the default first prompt message (unresolved feature prompt) with the user message
      );
    },
    [generateSubmissionAIThread, createAIThread, submission, localSelectedFiles, aiFeature, quoteId],
  );

  const handleAIAssistantButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement> | undefined) => {
      // When opening the popup in file collection mode,
      // we want to restore the original selection mode of the files explorer if it was changed
      // when we enter the file collection mode
      if (!isMenuOpen) {
        if (isInFileCollectionMode) {
          setLocalSelectedFiles(filesExplorer.selectedFiles);

          // Reset local file collection mode flag
          setIsInFileCollectionMode(false);

          // Reset backup
          setOriginalSelectionMode(undefined);

          // Restore original selection mode of the files explorer
          if (originalSelectionMode !== undefined) {
            setFilesExplorer({
              selectionMode: originalSelectionMode,
              // Only clear selected files if the original selection mode was false
              ...(originalSelectionMode === false && { selectedFiles: [] }),
            });
          }
        }

        openMenu(event);
      }
    },
    [
      setFilesExplorer,
      originalSelectionMode,
      openMenu,
      isMenuOpen,
      isInFileCollectionMode,
      filesExplorer.selectedFiles,
    ],
  );

  const handleCloseMenu = useCallback(() => {
    resetTriggerState();
    setShouldAutoRun(false);
    closeMenu();
  }, [closeMenu, resetTriggerState]);

  const isIconButtonTooltipOpen = isInFileCollectionMode || (isLoading && !isContentStreaming && !isMenuOpen);

  return (
    <Box
      zIndex={(theme) => theme.zIndex.tooltip}
      bgcolor="green.300"
      color="common.black"
      borderRadius="50%"
      boxShadow={softShadow}
    >
      {isLoading && (
        <Box position="absolute">
          <CircularProgress sx={{ color: 'text.secondary' }} thickness={2} />
        </Box>
      )}
      <IconButton
        ref={buttonRef}
        isTooltipOpen={isIconButtonTooltipOpen}
        tooltipCustomColor="green.300"
        tooltipPlacement="left"
        tooltipContent={
          isIconButtonTooltipOpen ? (
            <Stack flexDirection="row" flex={1}>
              <Icon component={AutoAwesomeOutlined} sx={{ fontSize: 18, mr: 1 }} />
              <Box>
                {isInFileCollectionMode
                  ? messages.submissionWorkspace.submissionAiAssistant.selectFilesToAttach
                  : messages.submissionWorkspace.submissionAiAssistant.generatingResponse}
              </Box>
            </Stack>
          ) : undefined
        }
        color="inherit"
        icon={AutoAwesomeOutlined}
        onClick={handleAIAssistantButtonClick}
      />

      {isMenuOpen && (
        <AIPopup
          popupTitle={messages.submissionWorkspace.submissionAiAssistant.popupTitle}
          withFileLinks
          isRichTextResponse
          anchorEl={anchorEl}
          isMenuOpen={isMenuOpen}
          closeMenu={handleCloseMenu}
          popupMaxHeight={popupMaxHeight}
          popupMinHeight={popupMinHeight}
          popupWidth={popupWidth}
          threadId={threadId}
          openMenu={openMenu}
          regenerateButtonMode={RegenerateButtonMode.Restart}
          regenerateButtonHandler={handleRegenerate}
          withEmptyState={false}
          addFollowupMessageCustomHandler={emptyMessages ? handleFirstFollowupMessage : undefined}
          attachFilesButtonHandler={handleAttachFiles}
          selectedFilesCount={localSelectedFiles.length}
          initialQuestion={initialQuestion}
          autoRun={shouldAutoRun}
        />
      )}
    </Box>
  );
}
